/**
 * Event Bus - Cross-component communication system
 * Provides pub/sub pattern for decoupled component communication
 */

const EventBus = {
  _handlers: new Map(),

  /**
   * Subscribe to an event
   * @param {string} event - Event name
   * @param {Function} handler - Handler function
   * @returns {Function} Unsubscribe function
   */
  on(event, handler) {
    if (!this._handlers.has(event)) {
      this._handlers.set(event, new Set());
    }
    this._handlers.get(event).add(handler);

    // Return unsubscribe function
    return () => this.off(event, handler);
  },

  /**
   * Subscribe to an event once
   * @param {string} event - Event name
   * @param {Function} handler - Handler function
   */
  once(event, handler) {
    const wrapper = (data) => {
      handler(data);
      this.off(event, wrapper);
    };
    this.on(event, wrapper);
  },

  /**
   * Unsubscribe from an event
   * @param {string} event - Event name
   * @param {Function} handler - Handler function
   */
  off(event, handler) {
    const handlers = this._handlers.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  },

  /**
   * Emit an event
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    const handlers = this._handlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for "${event}":`, error);
        }
      });
    }

    // Also dispatch as DOM CustomEvent for backward compatibility
    window.dispatchEvent(new CustomEvent(event, { detail: data }));
  },

  /**
   * Remove all handlers for an event
   * @param {string} event - Event name
   */
  clear(event) {
    this._handlers.delete(event);
  },

  /**
   * Remove all event handlers
   */
  clearAll() {
    this._handlers.clear();
  }
};

/**
 * Standard event names used throughout the application
 */
const Events = {
  // Navigation
  NAVIGATION_CHANGE: 'navigationChange',
  STEP_COMPLETED: 'stepCompleted',

  // Progress
  PROGRESS_UPDATED: 'progressUpdated',
  PROGRESS_RESET: 'progressReset',
  MILESTONE_REACHED: 'milestoneReached',

  // User/Auth
  AUTH_STATE_CHANGED: 'authStateChanged',

  // UI
  LANGUAGE_CHANGED: 'languageChanged',
  CONFIG_CHANGED: 'configChanged',
  THEME_CHANGED: 'themeChanged',

  // Modals
  MODAL_OPEN: 'modalOpen',
  MODAL_CLOSE: 'modalClose',

  // Notifications
  TOAST_SHOW: 'toastShow',
  TOAST_HIDE: 'toastHide',

  // App State
  APP_INITIALIZED: 'appInitialized',
  APP_ERROR: 'appError',

  // Sidebar
  SIDEBAR_TOGGLE: 'sidebarToggle',

  // Assessment
  ASSESSMENT_ANSWER: 'assessmentAnswer'
};

// Make available globally for legacy code (inline-app.js, etc.)
window.EventBus = EventBus;
window.Events = Events;

// ES module exports
export { EventBus, Events };
